import {
  Box,
  GlobalStyles,
  Stack,
  StyledEngineProvider,
  ThemeProvider,
  Typography,
} from '@mui/material';
import { theme } from './common/theme';
import './logged-out.css';
import { images } from './assets/images';
import { LoadingButton } from '@mui/lab';
import SignUpForm from './components_v2/forms/SignUpForm';
import { useAppDispatch, useAppSelector } from './redux/store';
import { useEffect, useMemo, useRef } from 'react';
import { ContinueRef } from './onboarding/types';
import { OnboardingV2Step } from './redux/onboardingTypes';
import SelectCountryForm from './components_v2/forms/SelectCountryForm';
import CountryForm from './components_v2/forms/CountryForm';
import GenderForm from './components_v2/forms/GenderForm';
import SummaryV2Form from './components_v2/forms/SummaryV2Form';
import { onboardingActions } from './redux/onboarding';
import AddressV2 from './components_v2/forms/AddressvV2';
import SYDLeftPanelContainer from './components_v2/SYDLeftPanelContainer';
import StripeForm from './components_v2/forms/StripeForm';
import { useSearchParams } from 'react-router-dom';
import CheckoutForm from './components_v2/forms/CheckoutForm';
import { useTranslation } from 'react-i18next';
import { useAppTransitions } from './ui';
import HeaderProgressBar from './components_v2/header/HeaderPogressBar';
import { useLoggedOutHeader } from './headerHooks';
import SecondForm from './components_v2/forms/SecondForm';
import { appEnvironment } from './common/environment';

export default function LoggedOutV2() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  // --- Redux State ---
  const { buttonEnabled, onboardingStep, showingMessages } = useAppSelector(
    (state) => state.onboarding
  );
  const loading = useAppSelector((state) => state.checkout.loading);

  // --- Animation & Refs ---
  const leftPanelImageRef = useRef<HTMLImageElement>(null);
  const ref = useRef<ContinueRef>(null);
  const hasRunAnimation = useRef(false);

  const { leftPanelTranslationX, animatedRightPanelInOnboarding } =
    useAppTransitions(leftPanelImageRef, true);

  // --- Header Logic ---
  const { step, show, subtext } = useLoggedOutHeader();

  // --- Form Resolution ---
  const rightView = useMemo(() => {
    switch (onboardingStep) {
      case OnboardingV2Step.SECOND_FORM:
        return <SecondForm ref={ref} />;
      case OnboardingV2Step.COUNTRY:
        return <CountryForm ref={ref} />;
      case OnboardingV2Step.COUNTRY_SELECT:
        return <SelectCountryForm />;
      case OnboardingV2Step.GENDER:
        return <GenderForm ref={ref} />;
      case OnboardingV2Step.BILLING_ADDRESS:
        return <AddressV2 ref={ref} type="billing" />;
      case OnboardingV2Step.DELIVERY_ADDRESS:
        return <AddressV2 ref={ref} type="delivery" />;
      case OnboardingV2Step.PAYMENT_INFO:
        return <StripeForm ref={ref} />;
      case OnboardingV2Step.SUMMARY:
        return <SummaryV2Form ref={ref} />;
      case OnboardingV2Step.SIGN_UP:
      default:
        return <SignUpForm ref={ref} />;
    }
  }, [onboardingStep]);

  const stepTitle = useMemo(() => {
    switch (onboardingStep) {
      case OnboardingV2Step.SIGN_UP:
        return t('completeTheFieldsBelow').replace(
          /syd/gi,
          appEnvironment.theme.appName
        );
      case OnboardingV2Step.SECOND_FORM:
        return t('yourDateOfBirth');
      case OnboardingV2Step.DELIVERY_ADDRESS:
        return t('whereWouldWeShipYourGeneticsTestingKit');
      default:
        return undefined;
    }
  }, [onboardingStep, t]);

  // --- Effects ---
  useEffect(() => {
    if (
      !appEnvironment.theme.showOnboardingAnimation &&
      onboardingStep === OnboardingV2Step.ANIMATING
    ) {
      dispatch(onboardingActions.setButtonEnabled(true));
      dispatch(onboardingActions.setOnboardingStep(OnboardingV2Step.SIGN_UP));
    }
  }, [onboardingStep, dispatch]);

  useEffect(() => {
    if (
      !hasRunAnimation.current &&
      onboardingStep === OnboardingV2Step.WELCOME
    ) {
      hasRunAnimation.current = true;
      animatedRightPanelInOnboarding();
      dispatch(onboardingActions.setButtonEnabled(true));
      dispatch(onboardingActions.setOnboardingStep(OnboardingV2Step.INTRO));
    }
  }, [animatedRightPanelInOnboarding, dispatch, onboardingStep]);

  // --- Render Helpers ---

  const isAnimatingPhase = [
    OnboardingV2Step.ANIMATING,
    OnboardingV2Step.INTRO,
    OnboardingV2Step.WELCOME,
  ].includes(onboardingStep);

  const leftPanel = useMemo(() => {
    if (!appEnvironment.theme.showOnboardingAnimation) {
      return null;
    }

    return (
      <Stack
        className={`fixed top-0 left-0 w-full h-full z-[-2] ${
          !isAnimatingPhase ? '' : 'transition-transform duration-1000'
        }`}
        justifyContent="center"
        alignItems="center"
        sx={{
          transform: `translateX(${leftPanelTranslationX}px)`,
        }}
      >
        <SYDLeftPanelContainer imageRef={leftPanelImageRef} />
      </Stack>
    );
  }, [isAnimatingPhase, leftPanelTranslationX]);

  const rightPanel = useMemo(() => {
    if (isAnimatingPhase) {
      return null;
    }

    return (
      <Stack
        direction="row"
        justifyContent="center"
        className="relative z-10 w-full mb-[10px] min-h-full"
      >
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          className="macbook13:mt-[0px] w-[700px]"
        >
          {rightView}
        </Stack>
      </Stack>
    );
  }, [isAnimatingPhase, rightView]);

  const footer = useMemo(() => {
    if (onboardingStep === OnboardingV2Step.ANIMATING) {
      return (
        <Typography className="font-medium text-[14px] text-[#26262680] my-10 self-center z-20 relative">
          {t('establishingPrivateAndSecureConnection')}
        </Typography>
      );
    }

    return (
      <Stack
        className="macbook13:my-0 macbook13:mb-[20px] my-[20px] z-20 relative"
        alignItems={'center'}
      >
        <span className="text-[#757D87] text-[12px] font-normal max-w-[700px] macbook13:hidden">
          {t('disclaimer').replace(/syd/gi, appEnvironment.theme.appName)}
        </span>

        <Typography component={'span'} className="signUp-note dark">
          <Typography component={'span'} className="signUp-note">
            98%
          </Typography>{' '}
          {t('percentOfMembers').replace(/syd/gi, appEnvironment.theme.appName)}
        </Typography>

        <LoadingButton
          variant="contained"
          endIcon={
            loading || showingMessages ? undefined : (
              <Box component="img" src={images.v2.button.tick} />
            )
          }
          className={`button large bg-text ${
            onboardingStep !== OnboardingV2Step.WELCOME
              ? 'opacity-100'
              : 'opacity-0'
          }`}
          onClick={() => {
            if (onboardingStep !== OnboardingV2Step.PAYMENT_INFO) {
              dispatch(onboardingActions.setButtonEnabled(false));
            }
            if (ref.current) {
              ref.current.continuePressed();
            }
          }}
          loading={loading || showingMessages}
          disabled={!buttonEnabled || showingMessages}
        >
          {onboardingStep === OnboardingV2Step.SUMMARY
            ? t('completeSignUp')
            : t('continue')}
        </LoadingButton>
      </Stack>
    );
  }, [buttonEnabled, dispatch, loading, onboardingStep, showingMessages, t]);

  return (
    <StyledEngineProvider injectFirst={true}>
      <ThemeProvider theme={theme}>
        <GlobalStyles
          styles={{ body: { background: '#F5F6F6', padding: 0, margin: 0 } }}
        />

        <Stack className="root-container">
          {/** Top container - logo and top bar */}
          <Stack className="relative w-full justify-center my-[20px] z-20">
            <Stack
              justifyContent={'center'}
              alignItems={'center'}
              className="absolute left-[40px] top-0 logo-container"
            >
              <Box
                component="img"
                src={appEnvironment.theme.images.logo}
                className="max-w-[50px] macbook13:max-w-[40px]"
              />
            </Stack>

            <Box className={show ? 'opacity-100' : 'opacity-0 invisible'}>
              <HeaderProgressBar
                label={t('signUp')}
                step={step}
                totalSteps={2}
                subtext={subtext}
                blue={onboardingStep === OnboardingV2Step.SUMMARY}
                stepTitle={stepTitle}
              />
            </Box>
          </Stack>

          {/* Main Content Area */}
          <Stack direction="row" className="w-full flex-1 relative">
            {searchParams.get('payment_intent') !== null ? (
              <CheckoutForm />
            ) : (
              <>
                {leftPanel}
                {rightPanel}
              </>
            )}
          </Stack>

          {/* Footer / CTA */}
          {footer}

          {/* Background Logos */}
          <Stack
            direction={'row'}
            justifyContent={'space-between'}
            alignItems={'center'}
            className="fixed left-[20px] right-[40px] bottom-[20px] pointer-events-none z-[-1]"
          >
            <Box
              component={'img'}
              src={images.v2.onboarding.securityLogos}
              className="h-[40px]"
            />
            <Box
              component={'img'}
              className="h-[40px]"
              src={images.v2.onboarding.ratingLogos}
            />
          </Stack>
        </Stack>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
